/*
  Roots story
  ===========

  Chapters scroll; the photograph beside them changes to match.

  Driven by an IntersectionObserver on a narrow band across the middle of the
  viewport, not by a scroll handler. The band matters: a chapter becomes active
  when it reaches the middle of the screen, which is where a reader's eye
  actually is, rather than when its top edge touches the bottom of the viewport —
  which would swap the photograph while the chapter was still off-screen.

  The whole thing degrades to "the first photograph, still" if the script never
  runs: the first slide carries `is-active` from Liquid, and the chapters are an
  ordinary ordered list that reads correctly on its own.
*/

class RootsStory extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-story-slide]'));
    this.chapters = Array.from(this.querySelectorAll('[data-story-chapter]'));
    this.ticks = Array.from(this.querySelectorAll('[data-story-tick]'));
    if (this.chapters.length < 2) return;

    this.index = 0;

    /*
      -45% top and bottom leaves a 10%-tall band across the centre of the
      viewport. A chapter is active while it overlaps that band. Because chapters
      are taller than 10vh, exactly one overlaps it at a time for most of the
      scroll, and the observer fires only at the handful of moments that changes.
    */
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activate(Number(entry.target.dataset.index));
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    this.chapters.forEach((chapter) => this.observer.observe(chapter));
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  activate(index) {
    if (Number.isNaN(index) || index === this.index) return;
    this.index = index;

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });

    this.chapters.forEach((chapter, i) => {
      chapter.classList.toggle('is-active', i === index);
    });

    this.ticks.forEach((tick, i) => {
      tick.classList.toggle('is-active', i === index);
    });
  }
}

if (!customElements.get('roots-story')) {
  customElements.define('roots-story', RootsStory);
}
